import _ from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { Badge } from 'react-bootstrap';
import tinyColor from 'tinycolor2';
import { FieldDescriptor } from 'blaze-form';
import QuoteHistoryItem from '../QuoteHistoryItem';
import { flatten } from '../../../util/general';

const QuoteHistoryList = ({ history, states, layout, filter = {} }) => {
  // TODO don't pull from chat
  const { users } = useSelector(state => state.chat);

  const statusLookup = _.keyBy(states, 'id');
  const userLookup = _.keyBy(users, 'id');

  const getUserName = id => {
    const user = userLookup[id];

    if (id === 0) {
      return 'System';
    }

    if (!userLookup[id]) {
      return 'Removed User';
    }

    return `${user.firstName} ${user.lastName}`;
  };

  const formatArrayHistory = (values, checkAgainst) => {
    if (Array.isArray(values) && values.length) {
      const string = [];

      values.forEach((v, index) => {
        const checkAgainstValue = Array.isArray(checkAgainst) ? checkAgainst[index] || '' : '';

        if ((v || '') === checkAgainstValue) {
          return;
        }

        string.push(
          <div className="d-flex" key={String(index)}>
            &nbsp;
            <div className="rn">{index + 1}</div>
            &nbsp;{v}
          </div>
        );
        // string = string ? `${string} ${getCount(index + 1)} ${v}` : `${getCount(index + 1)} ${v}`;
      });
      return string;
    }

    return values;
  };

  const renderList = () => {
    let color = states[0] ? states[0].color : '#000';
    let currentState = states[0];
    const items = [];
    const filteredItems = [];

    history.forEach(item => {
      if (filter.statusValue !== 'all' && filter.statusValue !== currentState.id && item.attributeName !== 'Status') {
        filteredItems.push(item);
        return;
      }

      if (filter.assigneeValue !== 'all' && Number(filter.assigneeValue) !== item.createdBy) {
        filteredItems.push(item);
        return;
      }

      if (item.attributeName === 'Status' && states !== null) {
        const oldState = statusLookup[item.attributeOldValue];
        const newState = statusLookup[item.attributeNewValue];

        if (!newState) {
          return;
        }

        currentState = newState;
        color = newState.color;

        if (filter.statusValue !== 'all' && filter.statusValue !== newState.id) {
          return;
        }

        items.push(
          <QuoteHistoryItem
            key={`${String(item.id)}-status`}
            {...item}
            heading="Status Changed"
            createdBy={getUserName(item.createdBy)}
            color={color}
          >
            {oldState && (
              <>
                <span
                  className="old"
                  style={{
                    backgroundColor: oldState.color,
                    color: tinyColor(oldState.color).isDark() ? '#fff' : '#333',
                  }}
                >
                  {oldState ? oldState.name : item.attributeOldValue}
                </span>

                <i className="fas fa-arrow-alt-right change-arrow" />
              </>
            )}
            <span
              className="new"
              style={{ backgroundColor: newState.color, color: tinyColor(newState.color).isDark() ? '#fff' : '#333' }}
            >
              {newState ? newState.name : item.attributeNewValue}
            </span>
            <div className="p-1">{item.note}</div>
          </QuoteHistoryItem>
        );
        return;
      }

      if (item.attributeName === 'Assignee') {
        items.push(
          <QuoteHistoryItem
            key={`${String(item.id)}-assignee`}
            {...item}
            heading="Assignee Changed"
            createdBy={getUserName(item.createdBy)}
            color={color}
          >
            <span className="old">{getUserName(item.attributeOldValue)}</span>
            <i className="fas fa-arrow-alt-right change-arrow" />
            <span className="new">{getUserName(item.attributeNewValue)}</span>
          </QuoteHistoryItem>
        );

        return;
      }

      if (item.attributeName === 'Created') {
        items.push(
          <QuoteHistoryItem
            key={`${String(item.id)}-created`}
            {...item}
            heading="Created"
            createdBy={getUserName(item.createdBy)}
            color={color}
            hideBody
          />
        );
        return;
      }

      if (item.attributeName === 'Data') {
        const filtered = [];
        items.push(
          <QuoteHistoryItem
            key={`${String(item.id)}-data`}
            {...item}
            heading="Data Modified"
            createdBy={getUserName(item.createdBy)}
            color={color}
          >
            {Object.keys(flatten(item.attributeNewData)).map(k => {
              // Check Filters

              const fieldName = `data.${k}`;
              const field = _.get(layout.getLookup(), fieldName);
              if (!field) {
                return null;
              }
              const isAutoCalculated = !!field.calculated && fieldName !== 'data.retailPrice';
              const isHidden = !!field.hidden;

              if (filter.showCalculated === false && isAutoCalculated) {
                filtered.push(fieldName);
                return null;
              }

              if (filter.showHidden === false && isHidden) {
                filtered.push(fieldName);
                return null;
              }

              return (
                <div className="quote-data-entry" key={`${String(item.id)}-${k}`}>
                  <div className="quote-data-field">
                    {isAutoCalculated === true && (
                      <Badge title="Auto-calculated" variant="primary" className="mr-1">
                        <i className="fas fa-lambda" />
                      </Badge>
                    )}
                    <FieldDescriptor fieldName={fieldName} lookup={layout.getLookup()} />
                  </div>
                  <div className="quote-data-changes">
                    <span className="old">
                      {formatArrayHistory(_.get(item.attributeOldData, k), _.get(item.attributeNewData, k))}
                    </span>
                    <i className="fas fa-arrow-alt-right change-arrow" />
                    <span className="new">
                      {formatArrayHistory(_.get(item.attributeNewData, k), _.get(item.attributeOldData, k))}
                    </span>
                  </div>
                </div>
              );
            })}
            {filtered.length > 0 && (
              <div className="filtered-count">
                <div className="info-sect">
                  <div>
                    <i className="fas fa-info-circle" /> <strong>{filtered.length}</strong> datapoints filtered
                  </div>
                </div>
              </div>
            )}
          </QuoteHistoryItem>
        );
        return;
      }

      items.push(
        <QuoteHistoryItem
          key={`${String(item.id)}-attr`}
          {...item}
          heading={item.attributeName}
          color={color}
          createdBy={getUserName(item.createdBy)}
        >
          <span className="old">{item.attributeOldValue}</span>
          <i className="fas fa-arrow-alt-right change-arrow" />
          <span className="new">{item.attributeNewValue}</span>
        </QuoteHistoryItem>
      );
    });

    return items.reverse();
  };

  return <div className="quote-history-list">{renderList()}</div>;
};

export default QuoteHistoryList;
